import { useMemo } from 'react'
import OrderStatusEnum from '@enums/OrderStatusEnum'
import IOrder from '@interfaces/IOrder'

const STATUS_ORDER = [
  OrderStatusEnum.RESERVATION_REQUEST,
  OrderStatusEnum.PHOTOMATCH_IN_ANALYSIS,
  OrderStatusEnum.WAITING_PAYMENT,
  OrderStatusEnum.WAITING_PAYMENT_TRANSPORT,
  OrderStatusEnum.WAITING_PAYMENT_VERIFICATION,
  OrderStatusEnum.SEND_CONTRACT,
  OrderStatusEnum.WAITING_SIGNATURE,
  OrderStatusEnum.CONTRACT_SIGNED,
  OrderStatusEnum.REQUEST_FOR_FORM,
  OrderStatusEnum.WAITING_SEND_REQUEST,
  OrderStatusEnum.SEND_SCHEDULED,
  OrderStatusEnum.SENDED,
  OrderStatusEnum.DELIVERED,
  OrderStatusEnum.AFTER_SALES,
]

const SUBSTATUS_ORDER = [
  OrderStatusEnum.PENDING,
  OrderStatusEnum.GENETICS,
  OrderStatusEnum.MAINTENANCE_FEE,
  OrderStatusEnum.RESERVATION_FEE,
  OrderStatusEnum.CUSTOM_IMPORT,
]

export const isOrderInSubstatus = (status: OrderStatusEnum): boolean => {
  return SUBSTATUS_ORDER.includes(status)
}

export const useOrderProgress = (inProgressOrder: IOrder | null) => {
  const stepProgress = useMemo(() => {
    if (!inProgressOrder?.id) return Array(10).fill(0)

    let status = inProgressOrder.status as OrderStatusEnum

    if (SUBSTATUS_ORDER.includes(status)) {
      status = inProgressOrder.previous_status as OrderStatusEnum
    }

    const currentStatusIndex = STATUS_ORDER.indexOf(status)

    const progress = [
      100, // Solicitação de Reserva + Photomatching
      (status === OrderStatusEnum.RESERVATION_REQUEST || status === OrderStatusEnum.PHOTOMATCH_IN_ANALYSIS) ? 50 : 100, // Aguardando Solicitação de Reserva
      status === OrderStatusEnum.WAITING_PAYMENT ? 50 : (currentStatusIndex > STATUS_ORDER.indexOf(OrderStatusEnum.WAITING_PAYMENT) ? 100 : 0), // Pagamentos e Comprovantes
      status === OrderStatusEnum.WAITING_PAYMENT_TRANSPORT ? 50 : (currentStatusIndex > STATUS_ORDER.indexOf(OrderStatusEnum.WAITING_PAYMENT_TRANSPORT) ? 100 : 0), // Pagamentos e Comprovantes
      status === OrderStatusEnum.WAITING_PAYMENT_VERIFICATION ? 50 : (currentStatusIndex > STATUS_ORDER.indexOf(OrderStatusEnum.WAITING_PAYMENT_VERIFICATION) ? 100 : 0), // Verificação de Pagamento
      status === OrderStatusEnum.SEND_CONTRACT ? 50 : (currentStatusIndex > STATUS_ORDER.indexOf(OrderStatusEnum.SEND_CONTRACT) ? 100 : 0), // Preenchimento do Contrato
      status === OrderStatusEnum.WAITING_SIGNATURE ? 50 : (currentStatusIndex > STATUS_ORDER.indexOf(OrderStatusEnum.WAITING_SIGNATURE) ? 100 : 0), // Assinatura do Contrato
      status === OrderStatusEnum.REQUEST_FOR_FORM ? 50 : (currentStatusIndex > STATUS_ORDER.indexOf(OrderStatusEnum.REQUEST_FOR_FORM) ? 100 : 0), // Aguardando Solicitação de Envio da Clínica
      status === OrderStatusEnum.SEND_SCHEDULED ? 100 : (currentStatusIndex > STATUS_ORDER.indexOf(OrderStatusEnum.SEND_SCHEDULED) ? 100 : 0), // Envio da Amostra de Sêmen Agendado
      (status === OrderStatusEnum.SENDED || status === OrderStatusEnum.DELIVERED || status === OrderStatusEnum.AFTER_SALES) ? 100 : 0, // Enviado para Clínica
    ]

    // TCLE precede a Confirmação de Reserva: enquanto o TCLE é exigido e ainda não
    // foi aceito (e a reserva não foi efetivada), a Confirmação de Reserva fica zerada.
    if (inProgressOrder.requires_tcle && !inProgressOrder.tcle_accepted_at && !inProgressOrder.selected_donor) {
      progress[1] = 0
    }

    return progress
  }, [inProgressOrder])

  const tcleStepProgress = useMemo<number | null>(() => {
    if (!inProgressOrder?.id || !inProgressOrder.requires_tcle) return null

    // Reservation effected in the ERP = selected_donor set. Single source of truth,
    // mirroring the backend guard in OrderController::fillTcleForm (selected_donor_id !== null).
    const reservationEffected = !!inProgressOrder.selected_donor

    return reservationEffected ? 100 : (inProgressOrder.tcle_accepted_at ? 50 : 0)
  }, [inProgressOrder])

  const totalProgress = useMemo(() => {
    const steps = tcleStepProgress === null ? stepProgress : [...stepProgress, tcleStepProgress]
    const progressSum = steps.reduce((sum, progress) => sum + progress, 0)
    return Math.round(progressSum / steps.length)
  }, [stepProgress, tcleStepProgress])

  return { stepProgress, tcleStepProgress, totalProgress }
}
